import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/utils/trpc';

export interface TagMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function slugifyTag(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/g, '-');
}

export function TagMultiSelect({
  value,
  onChange,
  placeholder = 'Adicionar tag...',
  disabled,
  className,
}: TagMultiSelectProps) {
  const [inputValue, setInputValue] = useState('');

  const suggestions = useQuery({
    ...trpc.tags.suggest.queryOptions({ q: inputValue }),
    enabled: !disabled && inputValue.trim().length > 0,
  });
  const topTags = useQuery({
    ...trpc.tags.list.queryOptions({}),
    enabled: !disabled && inputValue.trim().length === 0,
  });

  const suggestionItems = useMemo(() => {
    if (inputValue.trim().length > 0) return suggestions.data || [];
    return (topTags.data || []).slice(0, 10);
  }, [inputValue, suggestions.data, topTags.data]);

  const addTag = (raw: string) => {
    const slug = slugifyTag(raw);
    if (!slug) return;
    if (value.includes(slug)) return;
    onChange([...value, slug]);
    setInputValue('');
  };

  const removeTag = (slug: string) => {
    onChange(value.filter((t) => t !== slug));
  };

  return (
    <div className={className}>
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {value.map((tag) => (
            <Badge
              className="flex items-center gap-1"
              key={tag}
              variant="secondary"
            >
              #{tag}
              <button
                aria-label={`Remover tag ${tag}`}
                className="rounded p-0.5 hover:text-red-500"
                onClick={() => removeTag(tag)}
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          disabled={disabled}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag(inputValue);
            }
          }}
          placeholder={placeholder}
          value={inputValue}
        />
        <Button
          disabled={disabled}
          onClick={() => addTag(inputValue)}
          size="sm"
          type="button"
          variant="outline"
        >
          Adicionar
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {suggestionItems.map((t: any) => (
          <Button
            disabled={disabled}
            key={t.tag}
            onClick={() => addTag(t.tag)}
            size="sm"
            type="button"
            variant="secondary"
          >
            #{t.tag}
          </Button>
        ))}
      </div>
    </div>
  );
}
